import React, { useCallback, useEffect, useRef, useState } from "react";
import { ControlsContainer } from "./styles";
import { PrimaryButton } from "../PrimaryButton";
import repeatIcon from "../../assets/icons/repeat.svg";
import speedIcon from "../../assets/icons/speed.svg";
import pauseIcon from "../../assets/icons/pause.svg";
import playIcon from "../../assets/icons/play.svg";
import { AnimationData } from "../../assets/glb/animations";

interface AnimationControlsProps {
  repeat: boolean;
  headerAnimation: AnimationData[];
  animation: AnimationData[];
  setRepeat: React.Dispatch<React.SetStateAction<boolean>>;
  setAnimation: React.Dispatch<React.SetStateAction<AnimationData[]>>;
  animationSpeed: number;
  setSpeed: React.Dispatch<React.SetStateAction<number>>;
  isPaused: boolean;
  setIsPaused: React.Dispatch<React.SetStateAction<boolean>>;
  playerTimeMs: number; 
  totalDuration: number; 
}

export function AnimationControls({
  repeat,
  setRepeat,
  setAnimation,
  headerAnimation,
  animation,
  animationSpeed,
  setSpeed,
  isPaused,
  setIsPaused,
  playerTimeMs,
  totalDuration
}: AnimationControlsProps) {
  const [animating, setAnimating] = useState(false);
  const [showSpeedTooltip, setShowSpeedTooltip] = useState(false);
  const [progress, setProgress] = useState(0);
  const pendingSpeedRef = useRef<number | null>(null);

  useEffect(() => {
    if (animation.length > 0) {
      setAnimating(true);
      setIsPaused(false);
    } else {
      setAnimating(false);
      setProgress(0);
    }
  }, [animation]);

  useEffect(() => {
    if (totalDuration > 0) {
      const totalMs = totalDuration * 1000;
      const pct = Math.max(0, Math.min(100, (playerTimeMs / totalMs) * 100));
      setProgress(pct);

      if (pct >= 100 && !repeat) {
        setAnimating(false);
        setIsPaused(false);
        setProgress(0);
      }
    }
  }, [playerTimeMs, totalDuration, repeat]);

  function repeatAnimation() {
    if (headerAnimation?.length > 0) {
      setAnimation([...headerAnimation]);
    } else if (animation?.length > 0) {
      setAnimation([...animation]);
    }
    setRepeat(true);
    setAnimating(true);
    setIsPaused(false);
  }

  function speedUp() {
    setSpeed((prev) => {
      const next = prev < 2 ? +(prev + 0.5).toFixed(1) : 1;
      return next;
    });
    setShowSpeedTooltip(true);
    setTimeout(() => setShowSpeedTooltip(false), 1000);
  }

  const handleLibrasSelection = useCallback((detection: string) => {
    if (detection === 'parar') !animating ? repeatAnimation() : isPaused ? setIsPaused(false) : setIsPaused(true);
  }, [animating, isPaused]);

  useEffect(() => {
    const onLibrasEvent = (event: any) => handleLibrasSelection(event.detail.number);
    window.addEventListener("LIBRAS_SELECTION", onLibrasEvent);
    return () => window.removeEventListener("LIBRAS_SELECTION", onLibrasEvent);
  }, [handleLibrasSelection]);

  return (
    <ControlsContainer>
      <div className="buttons">
        {!animating ? (
          <PrimaryButton
            className="repeatButton button hint"
            data-hint="Repetir"
            data-hint-placement="top-left"
            onClick={repeatAnimation}
            id="repeatButton"
            type="button"
          >
             <img src={repeatIcon} alt="Repetir" />
          </PrimaryButton>
        ) : (
          <PrimaryButton className="playPauseButton button hint" 
          onClick={() => setIsPaused(!isPaused)} 
          id="playPauseButton" 
          type="button"
          data-hint={isPaused ? "Play" : "Pausar"}
          >
            <img src={isPaused ? playIcon : pauseIcon} alt="Play/Pause" />
          </PrimaryButton>
        )}
      </div>

      <div className="progressWrapper" aria-hidden>
        <div className="progressBar">
          <div className="progressFill" style={{ width: `${progress}%` }} />
          <div className="progressDot" style={{ left: `${progress}%` }} />
        </div>
      </div>

      <PrimaryButton
        type="button"
        className="button velocityButton hint"
        data-hint="Velocidade"
        data-hint-placement="top"
        onClick={speedUp}
      >
        <div className={`tooltip ${showSpeedTooltip ? "visible" : ""}`}>
          {animationSpeed.toFixed(1)}x
        </div>
        <img src={speedIcon} alt="Velocidade" />
      </PrimaryButton>
    </ControlsContainer>
  );
}